import { useState } from "react";
import { X, UploadCloud, FileText, Download } from "lucide-react";
import api from "../../api/axios";
import {
  T,
  Card,
  PrimaryButton,
  SecondaryButton,
} from "../../components/em/ui";

// Small CSV parser (handles quotes + escaped quotes). Same approach as the
// employee import — no need for a full RFC 4180 lib for one upload.
const parseCsv = (text) => {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cur.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (field !== "" || cur.length) {
        cur.push(field);
        rows.push(cur);
        cur = [];
        field = "";
      }
      if (ch === "\r" && text[i + 1] === "\n") i++;
    } else {
      field += ch;
    }
  }
  if (field !== "" || cur.length) {
    cur.push(field);
    rows.push(cur);
  }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim().toLowerCase());
  return rows.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = (r[idx] || "").trim();
    });
    return obj;
  });
};

const TEMPLATE =
  "name,designation,phone\n" +
  "Jane Doe,Software Engineer,9876543210\n" +
  "John Smith,Project Manager,9123456780\n";

const downloadTemplate = () => {
  const blob = new Blob([TEMPLATE], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "signatures-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

export default function SignatureCsvModal({ open, onClose, onImported }) {
  const [rows, setRows] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [parseErr, setParseErr] = useState(null);

  if (!open) return null;

  const handleFile = async (e) => {
    setParseErr(null);
    setReport(null);
    setRows(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (!parsed.length) {
        setParseErr("No rows found. Make sure the first row is a header.");
        return;
      }
      const ok = parsed.every((r) => r.name && r.designation && r.phone);
      if (!ok) {
        setParseErr(
          'Each row needs "name", "designation" and "phone" columns.',
        );
        return;
      }
      setRows(parsed);
    } catch (err) {
      setParseErr(err.message);
    }
  };

  const submit = async () => {
    if (!rows) return;
    setSubmitting(true);
    try {
      const res = await api.post("/signatures/bulk", { rows });
      setReport(res.data);
      if ((res.data.created || 0) + (res.data.updated || 0) > 0) onImported?.();
    } catch (err) {
      setParseErr(err?.response?.data?.error || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(3,2,19,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <Card
        padding={0}
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 480, overflow: "hidden" }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 20px",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: T.orange50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UploadCloud size={20} color={T.orange} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
              Bulk upload signatures
            </div>
            <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
              One signature per CSV row — generated automatically
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 0,
              background: "transparent",
              color: T.fg5,
              cursor: "pointer",
              display: "flex",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>
              Required columns: <b style={{ color: T.fg3 }}>name</b>,{" "}
              <b style={{ color: T.fg3 }}>designation</b>,{" "}
              <b style={{ color: T.fg3 }}>phone</b>
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                border: 0,
                background: "transparent",
                color: T.orange,
                cursor: "pointer",
                font: `600 12px ${T.font}`,
                whiteSpace: "nowrap",
              }}
            >
              <Download size={13} /> Template
            </button>
          </div>

          {/* Dropzone */}
          <label
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              padding: "28px 16px",
              border: `1.5px dashed ${T.border}`,
              borderRadius: T.rLg,
              background: T.bgHead,
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <FileText size={26} color={T.fg7} />
            <div style={{ font: `500 13px ${T.font}`, color: T.fg3 }}>
              Click to choose a CSV file
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              style={{ display: "none" }}
            />
            {rows && (
              <div style={{ font: `600 12px ${T.font}`, color: T.success600 }}>
                {rows.length} row{rows.length === 1 ? "" : "s"} ready to import
              </div>
            )}
          </label>

          {parseErr && (
            <div
              style={{
                marginTop: 12,
                padding: "9px 12px",
                borderRadius: T.rMd,
                background: "rgba(231,0,11,0.06)",
                border: `1px solid rgba(231,0,11,0.2)`,
                color: T.danger,
                font: `500 12px ${T.font}`,
              }}
            >
              {parseErr}
            </div>
          )}

          {report && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 14px",
                borderRadius: T.rMd,
                background: T.bgHead,
                border: `1px solid ${T.border}`,
                font: `400 13px ${T.font}`,
                color: T.fg3,
              }}
            >
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <span style={{ color: T.success600, fontWeight: 600 }}>
                  Created: {report.created || 0}
                </span>
                <span style={{ color: T.info600, fontWeight: 600 }}>
                  Updated: {report.updated || 0}
                </span>
                <span style={{ color: T.fg5, fontWeight: 600 }}>
                  Skipped: {report.skipped || 0}
                </span>
              </div>
              {report.errors?.length > 0 && (
                <ul
                  style={{
                    margin: "10px 0 0",
                    paddingLeft: 18,
                    color: T.danger,
                    font: `400 12px/1.6 ${T.font}`,
                  }}
                >
                  {report.errors.slice(0, 8).map((err, i) => (
                    <li key={i}>
                      Row {err.row}
                      {err.name ? ` (${err.name})` : ""}: {err.error}
                    </li>
                  ))}
                  {report.errors.length > 8 && (
                    <li>… and {report.errors.length - 8} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "14px 20px",
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <SecondaryButton onClick={onClose}>
            {report ? "Close" : "Cancel"}
          </SecondaryButton>
          {!report && (
            <PrimaryButton
              icon={UploadCloud}
              disabled={!rows || submitting}
              onClick={submit}
              style={!rows || submitting ? { opacity: 0.55, cursor: "default" } : {}}
            >
              {submitting ? "Importing…" : `Import${rows ? ` ${rows.length}` : ""}`}
            </PrimaryButton>
          )}
        </div>
      </Card>
    </div>
  );
}
